/**
 * EL PLANO DE LA CASA — el lienzo unificado (E1, 2026-08-22; F1 el mismo día).
 * =============================================================================
 *
 * Un solo SVG en METROS (una unidad = un metro) con los recintos de la planta activa,
 * los servicios fijos y el trazo en curso. Los objetos de los rubros llegan como capas
 * de la cotización (E2/F3). Este componente es el sustrato sobre el que todas dibujan.
 *
 * DECISIONES QUE NO SON DE GUSTO (plan de diseño §3 y propuesta F1):
 *
 * · **Pointer Events con captura.** Un gesto baja, se mueve o no, sube. Más de 6 px fue
 *   arrastre; si no, toque. Con `setPointerCapture` sigue aunque el dedo salga del SVG.
 *
 * · **El objetivo táctil se mide en píxeles, no en metros.** Radio de impacto 24 px ÷
 *   escala (px por metro): siempre cabe un dedo, encuadre el plano lo que encuadre.
 *   El imán de posición también: 10 px ÷ escala (tldraw hace lo mismo con 8 px).
 *
 * · **El viewBox tiene la proporción exacta del elemento.** Así dedo→metros es una
 *   regla de tres, y pan/zoom (dos dedos, rueda, arrastre con ratón en vacío) no
 *   acumulan error por el centrado de `preserveAspectRatio`.
 *
 * · **Un arrastre entero = un paso de deshacer.** Solo se escribe en `pointerup`, con su
 *   parche inverso. Durante el arrastre solo cambia la vista previa (`arrastre`).
 *
 * · **La forma rectangular es paramétrica, el polígono es la verdad.** Mover, estirar por
 *   lado o rotar reescriben `forma` y `poligono` juntos (`poligonoDeForma`), y lo que
 *   consume polígonos (trayectos, cobertura, Riego, Cámaras) no se entera.
 *
 * · **Un dedo sobre un recinto lo mueve; un dedo sobre vacío no hace pan** (Excalidraw
 *   arrastra ese conflicto en sus issues). Pan: dos dedos, rueda, o ratón en vacío.
 *   Un segundo dedo durante una rotación suelta los saltos de 15° («libre»).
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { caja, lados, dentro } from '../motor/geometria.js'
import {
  imantar, SERVICIOS, poligonoDeForma, medioDeLado, normalDeLado,
  medidaDesdePunto, redimensionarLado, anguloDesdePunto, imanAngulo,
  imanDeForma, imanDeLado, recintosSolapados, esContorno, PREFERENCIAS_PLANO
} from '../motor/plano.js'
import { CENTINELA_BORRAR } from '../datos/fusion.js'

const camino = pts => pts.length ? 'M' + pts.map(p => `${p.x} ${p.y}`).join(' L') + ' Z' : ''
const centroide = pts => {
  if (!pts.length) return { x: 0, y: 0 }
  const s = pts.reduce((a, p) => ({ x: a.x + p.x, y: a.y + p.y }), { x: 0, y: 0 })
  return { x: s.x / pts.length, y: s.y / pts.length }
}
const r1 = v => Math.round(v * 10) / 10

/**
 * Props:
 *   plano          versión 2 (ver motor/plano.js)
 *   planta         id de la planta activa
 *   recintoActivo  id del recinto seleccionado
 *   herramienta    'trazar' | 'editar' | 'servicio:<id>' | 'ver'
 *   trazo / setTrazo   el recinto en curso (vive en el padre)
 *   enfocar        { id, n } — centrar la vista en ese recinto cuando cambie `n`
 *   onParche(parche, inverso)
 *   onSeleccionar(id)
 */
export default function Plano({
  plano, planta, recintoActivo, herramienta = 'ver',
  trazo = [], setTrazo = () => {}, enfocar = null,
  marcadores = [], onColocar = () => {}, onQuitarMarcador = () => {}, onCerrarTrazo = () => {},
  onParche = () => {}, onSeleccionar = () => {}
}) {
  const svgRef = useRef(null)
  const gesto = useRef(null)
  const punteros = useRef(new Map())
  const [arrastre, setArrastre] = useState(null)
  const [cam, setCam] = useState(null)            // null = encuadre automático
  const [caja_, setCaja] = useState({ w: 800, h: 600 })

  /* Proporción real del elemento: el viewBox la copia. */
  useEffect(() => {
    const el = svgRef.current
    if (!el) return
    const medir = () => { const r = el.getBoundingClientRect(); if (r.width) setCaja({ w: r.width, h: r.height }) }
    medir()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null
    ro?.observe(el)
    return () => ro?.disconnect()
  }, [])
  const aspecto = caja_.h / caja_.w || 0.75

  /* Los contornos van primero para que queden debajo de los recintos. */
  const recintos = useMemo(
    () => Object.values(plano?.recintos || {}).filter(r => r.planta === planta && r.poligono?.length && r.visible !== false)
      .sort((a, b) => (esContorno(b) ? 1 : 0) - (esContorno(a) ? 1 : 0)),
    [plano, planta]
  )
  const servicios = useMemo(
    () => Object.entries(plano?.servicios || {}).filter(([, s]) => s && s.planta === planta),
    [plano, planta]
  )
  const esquinas = useMemo(() => recintos.flatMap(r => r.poligono), [recintos])

  /* Encuadre automático: todo lo de la planta más el trazo, con dos metros de aire. */
  const vistaAuto = useMemo(() => {
    const pts = [...esquinas, ...trazo, ...servicios.map(([, s]) => s)]
    let x, y, w
    if (pts.length < 2) { x = -1; y = -1; w = 14 } else {
      const b = caja(pts), m = 2
      w = Math.max(b.x1 - b.x0 + m * 2, (b.y1 - b.y0 + m * 2) / aspecto, 8)
      x = (b.x0 + b.x1) / 2 - w / 2
      y = (b.y0 + b.y1) / 2 - (w * aspecto) / 2
    }
    return { x, y, w, h: w * aspecto }
  }, [esquinas, trazo, servicios, aspecto])
  const vista = cam ? { ...cam, h: cam.w * aspecto } : vistaAuto

  useEffect(() => { setCam(null) }, [planta])

  /* Enfocar un recinto elegido en el panel de capas: si no se ve, la vista va a él. */
  useEffect(() => {
    if (!enfocar?.id) return
    const r = plano?.recintos?.[enfocar.id]
    if (!r?.poligono?.length) return
    const c = centroide(r.poligono)
    const m = vista.w * 0.08
    const fuera = c.x < vista.x + m || c.x > vista.x + vista.w - m || c.y < vista.y + m || c.y > vista.y + vista.h - m
    if (fuera) setCam({ x: c.x - vista.w / 2, y: c.y - vista.h / 2, w: vista.w })
  }, [enfocar?.n]) // eslint-disable-line react-hooks/exhaustive-deps

  const pxm = caja_.w / vista.w              // píxeles por metro
  const u = vista.w / 100                    // grosor base de trazo
  const tipo = u * 3.2
  const rManija = 7 / pxm

  /** Coordenada del dedo en metros (regla de tres exacta) y el radio de impacto de 24 px. */
  const leer = ev => {
    const r = svgRef.current.getBoundingClientRect()
    return {
      x: vista.x + (ev.clientX - r.left) * vista.w / r.width,
      y: vista.y + (ev.clientY - r.top) * vista.h / r.height,
      radio: 24 / pxm
    }
  }

  const activo = recintos.find(r => r.id === recintoActivo) || null
  const formaActiva = activo?.forma?.tipo === 'rect' ? activo.forma : null
  /* Solo en desarrollo: las pruebas de navegador leen la vista y el activo de aquí para
     convertir metros → píxeles con la misma regla de tres que `leer`. */
  if (import.meta.env.DEV) window.__plano = { vista, activo, herramienta, caja: caja_, gesto, arrastre, recintos }
  const puntoRotacion = f => {
    const n = normalDeLado(f, 0)
    const d = f.largo / 2 + 28 / pxm
    return { x: f.cx + n.x * d, y: f.cy + n.y * d }
  }

  /* ---------- Pan y zoom ---------- */

  const zoomEn = (factor, sx, sy) => {
    const r = svgRef.current.getBoundingClientRect()
    const W = { x: vista.x + (sx - r.left) * vista.w / r.width, y: vista.y + (sy - r.top) * vista.h / r.height }
    const w = Math.max(3, Math.min(300, vista.w / factor)), h = w * aspecto
    setCam({ x: W.x - (sx - r.left) * w / r.width, y: W.y - (sy - r.top) * h / r.height, w })
  }
  const rueda = ev => { ev.preventDefault(); zoomEn(Math.pow(1.0015, -ev.deltaY), ev.clientX, ev.clientY) }
  useEffect(() => {
    const el = svgRef.current
    if (!el) return
    el.addEventListener('wheel', rueda, { passive: false })
    return () => el.removeEventListener('wheel', rueda)
  })

  /* ---------- Gestos ---------- */

  const bajar = ev => {
    if (ev.button !== undefined && ev.button !== 0) return
    /* Dedos fantasma: si un `pointerup` se perdió (palma en la tablet, el navegador
       tomó el gesto), el mapa se quedaba con un dedo y TODO toque posterior se leía
       como pinch y se ignoraba. El dedo principal siempre empieza de cero. */
    if (ev.isPrimary !== false) punteros.current.clear()
    punteros.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY })
    try { ev.currentTarget.setPointerCapture(ev.pointerId) } catch { /* puntero sintético */ }

    if (punteros.current.size === 2) {
      const g = gesto.current
      if (g?.tomado && g.tomado.tipo === 'rotar') { g.libre = true; return }   // segundo dedo: rotación libre
      if (g?.tipo === 'uno' && g.tomado && g.movio) return                      // ya hay un arrastre: el segundo dedo no lo rompe
      const [a, b] = [...punteros.current.values()]
      const r = svgRef.current.getBoundingClientRect()
      const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      gesto.current = {
        tipo: 'pinch', d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, cam0: { ...vista },
        W0: { x: vista.x + (m.x - r.left) * vista.w / r.width, y: vista.y + (m.y - r.top) * vista.h / r.height }
      }
      setArrastre(null)
      return
    }
    if (punteros.current.size > 2) return

    const p = leer(ev)
    let tomado = null
    let objetivo = activo                        // el recinto sobre el que actúa el gesto
    if (herramienta === 'mano') tomado = { tipo: 'pan' }
    // 1) Las manijas del recinto activo mandan.
    if (!tomado && herramienta === 'editar' && activo && !activo.bloqueado) {
      if (formaActiva) {
        const f = formaActiva
        const pr = puntoRotacion(f)
        if (Math.hypot(pr.x - p.x, pr.y - p.y) <= p.radio) tomado = { tipo: 'rotar' }
        if (!tomado) {
          let mejor = null
          for (let i = 0; i < 4; i++) {
            const m = medioDeLado(f, i)
            const d = Math.hypot(m.x - p.x, m.y - p.y)
            if (d <= p.radio && (!mejor || d < mejor.d)) mejor = { tipo: 'lado', lado: i, d }
          }
          if (mejor) tomado = mejor
        }
      } else {
        let mejor = null
        activo.poligono.forEach((v, i) => {
          const d = Math.hypot(v.x - p.x, v.y - p.y)
          if (d <= p.radio && (!mejor || d < mejor.d)) mejor = { tipo: 'vertice', i, d }
        })
        if (mejor) tomado = mejor
      }
    }
    // 2) El cuerpo: SOLO el recinto seleccionado en el panel de capas se arrastra (modelo
    //    Photoshop — Juan, 2026-08-23: «forzosamente tengo que seleccionar la capa desde el
    //    selector; si no, en una tablet voy a estar seleccionando cosas que no quiero»).
    //    Tocar otro recinto no hace nada; el dedo nunca cambia la selección.
    if (!tomado && herramienta === 'editar' && activo && !activo.bloqueado && dentro(p, activo.poligono)) {
      tomado = { tipo: 'cuerpo' }
    }
    if (!tomado && herramienta !== 'trazar') {
      for (const [id, s] of servicios) {
        if (Math.hypot(s.x - p.x, s.y - p.y) <= p.radio) { tomado = { tipo: 'servicio', id }; break }
      }
    }
    // 3) Con ratón, arrastrar sobre el vacío (ni un recinto ni un contorno) mueve el plano.
    if (!tomado && ev.pointerType === 'mouse' && herramienta !== 'trazar' && !herramienta.startsWith('servicio:')
        && !recintos.some(r => dentro(p, r.poligono))) {
      tomado = { tipo: 'pan' }
    }
    gesto.current = {
      tipo: 'uno', x0: ev.clientX, y0: ev.clientY, p0: p, tomado, movio: false, destino: null,
      objetivo, forma0: objetivo?.forma?.tipo === 'rect' ? objetivo.forma : null, cam0: { ...vista }, libre: false
    }
  }


  const mover = ev => {
    punteros.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY })
    const g = gesto.current
    if (!g) return
    if (g.tipo === 'pinch') {
      if (punteros.current.size < 2) return
      const [a, b] = [...punteros.current.values()]
      const r = svgRef.current.getBoundingClientRect()
      const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      const zoom = (Math.hypot(a.x - b.x, a.y - b.y) || 1) / g.d0
      const w = Math.max(3, Math.min(300, g.cam0.w / zoom)), h = w * aspecto
      setCam({ x: g.W0.x - (m.x - r.left) * w / r.width, y: g.W0.y - (m.y - r.top) * h / r.height, w })
      return
    }
    if (!g.tomado) return
    if (!g.movio && Math.hypot(ev.clientX - g.x0, ev.clientY - g.y0) < 6) return
    g.movio = true
    ev.preventDefault()

    if (g.tomado.tipo === 'pan') {
      const r = svgRef.current.getBoundingClientRect()
      const dx = (ev.clientX - g.x0) * g.cam0.w / r.width, dy = (ev.clientY - g.y0) * g.cam0.h / r.height
      setCam({ x: g.cam0.x - dx, y: g.cam0.y - dy, w: g.cam0.w })
      return
    }
    const p = leer(ev)
    let destino = null
    const obj = g.objetivo
    const otros = recintos.filter(r => r.id !== obj?.id)
    const radioIman = 10 / pxm
    if (g.tomado.tipo === 'cuerpo') {
      if (g.forma0) {
        const suelta = { ...g.forma0, cx: g.forma0.cx + p.x - g.p0.x, cy: g.forma0.cy + p.y - g.p0.y }
        const { forma, guias, iman } = imanDeForma(suelta, obj, otros, radioIman)
        destino = { forma, guias, iman }
      } else {
        const dx = r1(p.x - g.p0.x), dy = r1(p.y - g.p0.y)
        destino = { poligono: obj.poligono.map(v => ({ x: r1(v.x + dx), y: r1(v.y + dy) })) }
      }
    } else if (g.tomado.tipo === 'lado') {
      const lado = g.tomado.lado
      const campo = lado % 2 === 0 ? 'largo' : 'ancho'
      const { medida, guias } = imanDeLado(g.forma0, lado, medidaDesdePunto(g.forma0, lado, p), obj, otros, radioIman, PREFERENCIAS_PLANO, !!obj.candados?.[campo])
      destino = { forma: redimensionarLado(g.forma0, lado, medida), guias }
    } else if (g.tomado.tipo === 'rotar') {
      const { ang, iman } = imanAngulo(anguloDesdePunto(g.forma0, p), g.libre)
      destino = { forma: { ...g.forma0, ang }, iman }
    } else if (g.tomado.tipo === 'vertice') {
      destino = imantar(p, esquinas.filter(e => !obj.poligono.includes(e)), null)
    } else if (g.tomado.tipo === 'servicio') {
      destino = { x: r1(p.x), y: r1(p.y) }
    }
    g.destino = destino
    setArrastre({ ...g.tomado, ...destino, recinto: obj?.id })
  }

  const soltar = ev => {
    punteros.current.delete(ev.pointerId)
    const g = gesto.current
    if (!g) return
    if (g.tipo === 'pinch') { if (punteros.current.size < 2) gesto.current = null; return }
    gesto.current = null
    if (g.tomado && g.movio && g.destino) {
      const fin = g.destino
      const obj = g.objetivo
      if (fin.forma) {
        onParche({ recintos: { [obj.id]: { forma: fin.forma, poligono: poligonoDeForma(fin.forma) } } },
                 { recintos: { [obj.id]: { forma: g.forma0, poligono: obj.poligono } } })
      } else if (fin.poligono) {
        onParche({ recintos: { [obj.id]: { poligono: fin.poligono } } },
                 { recintos: { [obj.id]: { poligono: obj.poligono } } })
      } else if (g.tomado.tipo === 'vertice') {
        const antes = obj.poligono
        const despues = antes.map((v, i) => (i === g.tomado.i ? { x: fin.x, y: fin.y } : v))
        onParche({ recintos: { [obj.id]: { poligono: despues } } },
                 { recintos: { [obj.id]: { poligono: antes } } })
      } else if (g.tomado.tipo === 'servicio') {
        const antes = plano.servicios[g.tomado.id]
        onParche({ servicios: { [g.tomado.id]: { planta, x: fin.x, y: fin.y } } },
                 { servicios: { [g.tomado.id]: antes } })
      }
      setArrastre(null)
      return
    }
    setArrastre(null)
    if (g.movio) return
    // Toque.
    const p = leer(ev)
    if (herramienta === 'trazar') {
      // Tocar la primera esquina cierra el trazo (como en todos los editores de planos).
      if (trazo.length >= 3 && Math.hypot(trazo[0].x - p.x, trazo[0].y - p.y) <= p.radio) { onCerrarTrazo(); return }
      const anterior = trazo[trazo.length - 1] || null
      const q = imantar(p, esquinas, anterior)
      if (!cam) setCam({ x: vista.x, y: vista.y, w: vista.w })   // la vista no salta mientras se traza
      setTrazo([...trazo, { x: q.x, y: q.y }])
      return
    }
    if (herramienta === 'mano') return
    if (herramienta.startsWith('servicio:')) {
      const id = herramienta.slice('servicio:'.length)
      const antes = plano?.servicios?.[id]
      const q = { planta, x: r1(p.x), y: r1(p.y) }
      onParche({ servicios: { [id]: q } }, { servicios: { [id]: antes || CENTINELA_BORRAR } })
      return
    }
    // Tocar un marcador de la capa activa lo quita (en editar o colocando).
    if ((herramienta === 'colocar' || herramienta === 'editar') && activo) {
      const m = marcadores.filter(x => x.activo && x.recinto === activo.id)
        .map(x => ({ x, d: Math.hypot(x.x - p.x, x.y - p.y) })).filter(x => x.d <= p.radio * 0.8).sort((a, b) => a.d - b.d)[0]
      if (m) { onQuitarMarcador(m.x.objetoId, m.x.indice); return }
    }
    // 'colocar': dentro del recinto seleccionado cae un objeto; fuera, nada.
    if (herramienta === 'colocar' && activo && dentro(p, activo.poligono)) {
      onColocar(activo.id, { x: r1(p.x), y: r1(p.y) })
      return
    }
    // 'editar' o 'ver': un toque en el croquis NO selecciona. La selección vive en el panel de
    // capas (modelo Photoshop, decisión de Juan 2026-08-23).
  }

  /* ---------- Dibujo ---------- */

  const formaPrevia = arrastre?.forma || null
  const poligonoDe = r => {
    if (!arrastre || r.id !== (arrastre.recinto || activo?.id)) return r.poligono
    if (arrastre.forma) return poligonoDeForma(arrastre.forma)
    if (arrastre.poligono) return arrastre.poligono
    if (arrastre.tipo === 'vertice') return r.poligono.map((v, i) => (i === arrastre.i ? { x: arrastre.x, y: arrastre.y } : v))
    return r.poligono
  }
  const servicioEn = (id, s) => (arrastre?.tipo === 'servicio' && arrastre.id === id) ? { x: arrastre.x, y: arrastre.y } : s

  const lineas = []
  const paso = vista.w > 40 ? 5 : 1
  for (let x = Math.ceil(vista.x / paso) * paso; x <= vista.x + vista.w; x += paso) lineas.push({ v: true, p: x })
  for (let y = Math.ceil(vista.y / paso) * paso; y <= vista.y + vista.h; y += paso) lineas.push({ v: false, p: y })

  /* Cotas: siempre del activo (o del trazo); de todos cuando hay pocos recintos. */
  const acotar = []
  if (activo) acotar.push({ pts: poligonoDe(activo), fuerte: true })
  else if (trazo.length > 1) acotar.push({ pts: trazo, abierto: true, fuerte: true })
  if (recintos.length <= 6) recintos.filter(r => r.id !== activo?.id).forEach(r => acotar.push({ pts: r.poligono, fuerte: false }))

  const fActual = formaPrevia || formaActiva
  const mostrarManijas = herramienta === 'editar' && activo && !activo.bloqueado

  /* Solape (patrón magicplan): el recinto que pisa a otro se ve punteado en naranja, pero
     sigue siendo válido — frente al cliente no se bloquea nada. Con la vista previa incluida. */
  const solapados = useMemo(
    () => recintosSolapados(recintos.map(r => (r.id === activo?.id && arrastre) ? { ...r, poligono: poligonoDe(r) } : r)),
    [recintos, arrastre, activo] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const guias = arrastre?.guias || []

  return (
    <div className={`lienzo-plano ${herramienta === 'colocar' ? 'colocando' : ''}`}>
      <svg ref={svgRef}
        viewBox={`${vista.x} ${vista.y} ${vista.w} ${vista.h}`} preserveAspectRatio="xMidYMid meet"
        onPointerDown={bajar} onPointerMove={mover} onPointerUp={soltar} onPointerCancel={soltar}
        onLostPointerCapture={ev => { punteros.current.delete(ev.pointerId); if (gesto.current?.tipo === 'pinch' && punteros.current.size < 2) gesto.current = null }}
        role="application" aria-label="Plano de la casa">
        <g className="reticula">
          {lineas.map((l, i) => l.v
            ? <line key={i} x1={l.p} y1={vista.y} x2={l.p} y2={vista.y + vista.h} strokeWidth={u * .25} />
            : <line key={i} x1={vista.x} y1={l.p} x2={vista.x + vista.w} y2={l.p} strokeWidth={u * .25} />)}
        </g>

        {recintos.map(r => {
          const pts = poligonoDe(r)
          const c = centroide(pts)
          const esActivo = r.id === recintoActivo
          const contorno = esContorno(r)
          const fondo = contorno && activo && !esContorno(activo)     // trabajando un cuarto, los contornos se sombrean
          return (
            <g key={r.id} className={`recinto ${r.tipo} ${esActivo ? 'activo' : ''} ${r.bloqueado ? 'bloqueado' : ''} ${solapados.has(r.id) ? 'solapa' : ''} ${fondo ? 'fondo' : ''}`}>
              <path d={camino(pts)} strokeWidth={u * (contorno ? (r.tipo === 'construccion' ? 1.8 : 0.9) : esActivo ? 1.4 : 0.9)} />
              <text className="recinto-nombre" x={c.x} y={c.y} fontSize={tipo}
                textAnchor="middle" dominantBaseline="middle">{r.nombre}</text>
            </g>
          )
        })}

        {/* Guías del imán (verde = pegó): la banda del muro con su cota, la línea del
            contorno, o la esquina alineada. */}
        {guias.length > 0 && (
          <g className="guias">
            {guias.map((q, i) => {
              if (q.tipo === 'esquina') {
                return <line key={i} className="guia-linea" x1={q.p.x - q.n.x * 1.2} y1={q.p.y - q.n.y * 1.2}
                  x2={q.p.x + q.n.x * 1.2} y2={q.p.y + q.n.y * 1.2} strokeWidth={u * .5} />
              }
              const [p1, p2, p3, p4] = q.puntos
              if (q.tipo === 'contorno') {
                return <line key={i} className="guia-linea fuerte" x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} strokeWidth={u * 1.2} />
              }
              const c = { x: (p1.x + p3.x) / 2, y: (p1.y + p3.y) / 2 }
              return (
                <g key={i}>
                  <polygon className="guia-muro" points={[p1, p2, p3, p4].map(p => `${p.x},${p.y}`).join(' ')} strokeWidth={u * .3} />
                  <text className="guia-cota" x={c.x + q.n.x * u * 3.2} y={c.y + q.n.y * u * 3.2} fontSize={tipo * .8}
                    textAnchor="middle" dominantBaseline="middle">{q.muro.toFixed(2)}</text>
                </g>
              )
            })}
          </g>
        )}

        {trazo.length > 1 && (
          <polyline className="trazo" fill="none" strokeWidth={u * 1.2}
            points={trazo.map(p => `${p.x},${p.y}`).join(' ')} />
        )}
        {trazo.map((p, i) => (
          <circle key={`t-${i}`} className="punto-trazo" cx={p.x} cy={p.y} r={u * 1.3} strokeWidth={u * .4} />
        ))}

        {acotar.map((a, k) => lados(a.pts, !a.abierto).map(l => {
          const dx = l.b.x - l.a.x, dy = l.b.y - l.a.y
          const n = Math.hypot(dx, dy) || 1
          const off = u * 2.4
          return (
            <text key={`c-${k}-${l.i}`} className={`cota-txt ${a.fuerte ? '' : 'suave'}`}
              x={l.medio.x + (-dy / n) * off} y={l.medio.y + (dx / n) * off}
              fontSize={tipo * (a.fuerte ? .9 : .75)} textAnchor="middle" dominantBaseline="middle">{l.largo.toFixed(2)} m</text>
          )
        }))}

        {/* Manijas del recinto activo: cuatro de lado y una de rotación (forma), o esquinas (trazo). */}
        {mostrarManijas && fActual && (() => {
          const pr = puntoRotacion(fActual)
          const arriba = medioDeLado(fActual, 0)
          return (
            <g className="manijas">
              <line className="guia-rotacion" x1={arriba.x} y1={arriba.y} x2={pr.x} y2={pr.y} strokeWidth={u * .4} />
              {[0, 1, 2, 3].map(i => {
                const m = medioDeLado(fActual, i)
                return <rect key={i} className={`manija lado ${arrastre?.tipo === 'lado' && arrastre.lado === i ? 'activa' : ''}`}
                  x={m.x - rManija} y={m.y - rManija} width={rManija * 2} height={rManija * 2}
                  transform={`rotate(${fActual.ang || 0} ${m.x} ${m.y})`} strokeWidth={u * .4} />
              })}
              <circle className={`manija rotar ${arrastre?.tipo === 'rotar' ? 'activa' : ''}`} cx={pr.x} cy={pr.y} r={rManija * 1.1} strokeWidth={u * .4} />
              {(arrastre?.tipo === 'rotar' || (fActual.ang || 0) !== 0) && (
                <text className={`etiqueta-angulo ${arrastre?.iman ? 'iman' : ''}`} x={pr.x} y={pr.y - rManija * 2.6}
                  fontSize={tipo * .95} textAnchor="middle" dominantBaseline="middle">{Math.round((fActual.ang || 0) * 10) / 10}°</text>
              )}
            </g>
          )
        })()}
        {mostrarManijas && !fActual && poligonoDe(activo).map((v, i) => (
          <g key={`v-${i}`} className={`vertice ${arrastre?.tipo === 'vertice' && arrastre.i === i ? 'activo' : ''}`}>
            <circle className="punto" cx={v.x} cy={v.y} r={rManija} strokeWidth={u * .5} />
          </g>
        ))}

        {/* Objetos colocados con posición (F3): la capa activa en color, las demás tenues. */}
        {marcadores.map(m => (
          <g key={m.id} className={`marcador ${m.rubro} ${m.estado || ''} ${m.activo ? '' : 'tenue'}`}>
            <circle cx={m.x} cy={m.y} r={u * 1.7} strokeWidth={u * .35} />
            <text x={m.x} y={m.y} fontSize={tipo * .6} textAnchor="middle" dominantBaseline="central">{m.etiqueta}</text>
          </g>
        ))}

        {servicios.map(([id, s]) => {
          const q = servicioEn(id, s)
          const def = SERVICIOS.find(x => x.id === id)
          return (
            <g key={id} className={`servicio ${id}`}>
              <circle cx={q.x} cy={q.y} r={u * 2.4} strokeWidth={u * .4} />
              <text x={q.x} y={q.y} fontSize={tipo * .85} textAnchor="middle" dominantBaseline="central">{def?.corto || '•'}</text>
            </g>
          )
        })}
      </svg>
      {cam && (
        <button type="button" className="encuadrar" onClick={() => setCam(null)} aria-label="Encuadrar todo">Encuadrar</button>
      )}
    </div>
  )
}
